import { Checkmark } from "./Checkmark";
import { MONTHLY } from "../data/monthly";
import { T } from "../styles/theme";
import type { CheckedMap } from "../types";

interface MonthlyViewProps {
  checkedMonthly: CheckedMap;
  setCheckedMonthly: (next: (prev: CheckedMap) => CheckedMap) => void;
}

/** The "Monthly Anchors" tab — 4 week buckets of cross-cutting tasks. */
export function MonthlyView({ checkedMonthly, setCheckedMonthly }: MonthlyViewProps) {
  return (
    <div>
      <div
        style={{
          background: `linear-gradient(135deg,${T.goldPale},${T.white})`,
          borderRadius: 20,
          padding: "18px 18px 14px",
          marginBottom: 14,
          border: `1.5px solid ${T.gold}44`,
          boxShadow: `0 4px 20px ${T.gold}22`,
        }}
      >
        <div style={{ fontSize: 26, marginBottom: 4 }}>📅</div>
        <h2 style={{ margin: 0, fontSize: 20, color: T.clay, fontFamily: T.display, fontWeight: 700 }}>
          Monthly Anchors
        </h2>
        <p style={{ margin: "5px 0 0", fontSize: 12, color: T.inkMid, fontStyle: "italic", lineHeight: 1.5 }}>
          {MONTHLY.description}
        </p>
        <div
          style={{
            marginTop: 10,
            background: `${T.gold}18`,
            borderRadius: 10,
            padding: "9px 12px",
            fontSize: 11,
            color: T.inkMid,
            lineHeight: 1.5,
            border: `1px solid ${T.gold}33`,
          }}
        >
          💡 Spread across the month — nothing piles up. Check them off as you go.
        </div>
      </div>

      {MONTHLY.weeks.map((week) => {
        const wDone = week.tasks.filter((t) => checkedMonthly[t.id]).length;
        return (
          <div
            key={week.week}
            style={{
              background: T.white,
              borderRadius: 16,
              marginBottom: 10,
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(45,32,22,0.07)",
              border: `1px solid ${week.accent}33`,
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: week.color,
                borderBottom: `1px solid ${week.accent}22`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: week.accent, fontFamily: T.display }}>
                {week.week}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: week.accent,
                  background: "rgba(255,255,255,0.7)",
                  borderRadius: 99,
                  padding: "2px 8px",
                }}
              >
                {wDone}/{week.tasks.length}
              </span>
            </div>
            {week.tasks.map((task, i) => {
              const isDone = !!checkedMonthly[task.id];
              return (
                <div
                  key={task.id}
                  onClick={() => setCheckedMonthly((p) => ({ ...p, [task.id]: !p[task.id] }))}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    padding: "12px 16px",
                    gap: 11,
                    cursor: "pointer",
                    borderBottom: i < week.tasks.length - 1 ? `1px solid ${week.color}` : "none",
                    background: isDone ? "#f8fdf8" : T.white,
                    transition: "background 0.2s",
                    borderLeft: `3px solid ${isDone ? T.sage : week.accent}`,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginTop: 1,
                      border: isDone ? "none" : `2px solid ${week.accent}`,
                      background: isDone ? T.sage : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone && <Checkmark />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: isDone ? T.sage : T.ink,
                        textDecoration: isDone ? "line-through" : "none",
                        lineHeight: 1.45,
                      }}
                    >
                      {task.label}
                    </div>
                    <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 9,
                          color: week.accent,
                          background: week.color,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontWeight: 600,
                        }}
                      >
                        📆 {task.day}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          color: T.sageDark,
                          background: T.sagePale,
                          borderRadius: 6,
                          padding: "2px 7px",
                        }}
                      >
                        📌 {task.slot}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          color: T.inkLight,
                          background: T.parchment,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontStyle: "italic",
                        }}
                      >
                        ⏱ {task.duration}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
